import { authManager } from '@/shared/auth-manager';
import { MessageType, showErrorMessage, showMessage } from '@/shared/messages';
import { type ICockpitStore, store } from '@/store';
import { isAuthenticatedCockpitConfig } from '@/runtime-config';
import type { ICockpitConfig, ICockpitRuntimeConfig } from '@/types';
import { ErrorCode } from '@condo/domain';
import { ErrorTag } from '@condo/helpers';
import { ExternalApiSdk } from '@condo/sdk/external-api';
import { ListingPersisterSdk } from '@condo/sdk/listing-persister';
import { PipelineAuditPersisterSdk } from '@condo/sdk/pipeline-audit-persister';
import { PropxSdk } from '@condo/sdk/propx';
import { SourcingStorageSdk } from '@condo/sdk/sourcing-storage';
import { CalcengineSdkWrapper } from '@condo/sdk/calcengine';
import * as Sentry from '@sentry/vue';
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { isNil } from 'lodash-es';
import qs from 'qs';
import { Client as TypesenseClient } from 'typesense';
import { v4 as uuid } from 'uuid';
import type { Store } from 'vuex';
import { getInnerMostError } from '@condo/helpers';

export interface IApiClients {
    basecampMaintenance: AxiosInstance;
    basecamp: AxiosInstance;
    basecampPersister: AxiosInstance;
    salesPortal: AxiosInstance;
    discoverer: AxiosInstance;
    propx: PropxSdk;
    sourcingStorage: SourcingStorageSdk;
    listingPersister: ListingPersisterSdk;
    externalApi: ExternalApiSdk;
    /** Axios instance behind `externalApi`, for routes not yet on the generated SDK. */
    externalApiHttp: AxiosInstance;
    typesenseClient: TypesenseClient;
    pipelineAuditPersister: PipelineAuditPersisterSdk;
    themerger: AxiosInstance;
    calcengine: CalcengineSdkWrapper;
    hereCache: AxiosInstance;
}
declare global {
    interface Window {
        __CLIENTS__: IApiClients;
    }
}

const clients = {
    basecampMaintenance: import.meta?.hot?.data?.basecampMaintenance ?? (window as any).__BASECAMP_MAINTENANCE_CLIENT__ ?? null,
    basecamp: import.meta?.hot?.data?.basecamp ?? (window as any).__BASECAMP_CLIENT__ ?? null,
    basecampPersister: import.meta?.hot?.data?.basecampPersister ?? window.__CLIENTS__?.basecampPersister ?? null,
    salesPortal: import.meta?.hot?.data?.salesPortal ?? (window as any).__SALES_PORTAL_CLIENT__ ?? null,
    discoverer: import.meta?.hot?.data?.discoverer ?? (window as any).__DISCOVERER_CLIENT__ ?? null,
    propx: import.meta?.hot?.data?.propx ?? (window as any).__PROPX_CLIENT__ ?? null,
    sourcingStorage: import.meta?.hot?.data?.propx ?? (window as any).__SOURCING_STORAGE_CLIENT__ ?? null,
    listingPersister: import.meta?.hot?.data?.listingPersister ?? window.__CLIENTS__?.listingPersister ?? null,
    externalApi: import.meta?.hot?.data?.externalApi ?? window.__CLIENTS__?.externalApi ?? null,
    externalApiHttp: import.meta?.hot?.data?.externalApiHttp ?? window.__CLIENTS__?.externalApiHttp ?? null,
    typesenseClient: import.meta?.hot?.data?.typesenseClient ?? window.__CLIENTS__?.typesenseClient ?? null,
    pipelineAuditPersister: import.meta?.hot?.data?.pipelineAuditEventsClient ?? window.__CLIENTS__?.pipelineAuditPersister ?? null,
    themerger: import.meta?.hot?.data?.themerger ?? window.__CLIENTS__?.themerger ?? null,
    calcengine: import.meta?.hot?.data?.calcengine ?? window.__CLIENTS__?.calcengine ?? null,
    hereCache: import.meta?.hot?.data?.hereCache ?? window.__CLIENTS__?.hereCache ?? null,
} as IApiClients;

window.__CLIENTS__ = window.__CLIENTS__ ?? clients;

interface IDecodedCondoVersion {
    year: number;
    minor: number;
    patch: 'beta' | number;
    sha: string;
}

const decodeVersion = (version: string): IDecodedCondoVersion => {
    const [year, minor, _patch] = version.split('.');
    const [patch, sha] = _patch.split('-');

    return { year: +year, minor: +minor, patch: patch === 'beta' ? patch : +patch, sha } as IDecodedCondoVersion;
};

const compareVersions = (self: IDecodedCondoVersion, other: IDecodedCondoVersion) => {
    const isBeta = self.patch === 'beta' || other.patch === 'beta';
    const sameYear = self.year === other.year;
    const sameMinor = self.minor === other.minor;
    const samePatch = isBeta ? true : self.patch === other.patch;
    const sameSha = isBeta ? true : self.sha === other.sha;

    return sameYear && sameMinor && samePatch && sameSha;
};

const extendAxiosClient = (
    store: Store<ICockpitStore>,
    baseUrlProp:
        | 'S_BASECAMP_URL'
        | 'S_BASECAMP_MAINT_URL'
        | 'S_BASECAMP_PERSISTER_URL'
        | 'S_DISCOVERER_URL'
        | 'S_PROPX_URL'
        | 'S_SOURCING_STORAGE_URL'
        | 'S_SALES_PORTAL_API_URL'
        | 'S_LISTING_PERSISTER_URL'
        | 'S_EXTERNAL_API_URL'
        | 'S_PIPELINE_AUDIT_PERSISTER_URL'
        | 'S_THEMERGER_API_URL'
        | 'S_CALCENGINE_URL'
        | 'S_HERE_CACHE_URL',
    checkVersionCompat = true,
): AxiosInstance => {
    const client = axios.create({
        withCredentials: true,
        paramsSerializer: {
            serialize: params => qs.stringify(params, { arrayFormat: 'repeat' }),
        },
    });

    client.interceptors.request.use(config => {
        const appConfig = store.getters['generalData/config'] as ICockpitConfig;
        const impersonatedUserUid = store.getters['generalData/impersonatedUserId'];
        const dangerouslyEnableSideEffects = store.getters['generalData/dangerouslyEnableSideEffects'];

        config.baseURL = appConfig[baseUrlProp];
        return onRequest(impersonatedUserUid, dangerouslyEnableSideEffects)(config);
    });

    /**
     * Global error handling for Basecamp API
     */
    client.interceptors.response.use(response => {
        if (!checkVersionCompat) {
            return response;
        }
        const backendVersion = response.headers['x-condo-version'];
        if (backendVersion) {
            const appConfig = store.getters['generalData/config'] as ICockpitConfig;
            const decodedBackendVersion = decodeVersion(backendVersion);
            const cockpitVersion = decodeVersion(appConfig.HOUSEHOLD_VERSION);
            if (!compareVersions(cockpitVersion, decodedBackendVersion) && cockpitVersion.patch < decodedBackendVersion.patch) {
                showMessage(
                    `Your CCS-Version got outdated, please refresh your browser and make sure the version <b>👉 ${backendVersion} 👈</b> is shown on the bottom right`,
                    MessageType.Warning,
                    10_000,
                    backendVersion,
                );
            }
        }
        return response;
    }, onRejectedResponse);

    return client;
};

const initConfiguredHttpClients = (cockpitStore: Store<ICockpitStore> = store) => {
    const appConfig = cockpitStore.getters['generalData/config'] as ICockpitRuntimeConfig | undefined;
    if (!appConfig) {
        return;
    }

    initPublicHttpClients(cockpitStore);

    if (isAuthenticatedCockpitConfig(appConfig)) {
        initHttpClients(cockpitStore);
    }
};

/** Set to true on a specific request to skip attaching the auth token (e.g. for public routes like session-cookie login). */
export interface ICockpitAxiosRequestConfig extends AxiosRequestConfig {
    skipAuth?: boolean;
    /** When true, `onRejectedResponse` does not call `maybeShowAxiosError` — handle the error in the caller. */
    skipErrorHandler?: boolean;
}

const onRequest =
    (impersonatedUserUid: string, dangerouslyEnableSideEffects?: boolean) =>
    <T extends AxiosRequestConfig>(config: T): T | Promise<T> => {
        config.headers = config.headers ?? {};

        if (impersonatedUserUid) {
            config.headers['x-impersonate-user'] = impersonatedUserUid;
        }
        if (dangerouslyEnableSideEffects) {
            config.headers['x-dangerously-enable-side-effects'] = '1';
        }

        if (!config.headers['sentry-trace']) {
            const activeSpan = Sentry.getActiveSpan();
            if (activeSpan) {
                const sentryBaggageHeader = Sentry.spanToBaggageHeader(activeSpan);
                const sentryTraceHeader = Sentry.spanToTraceHeader(activeSpan);
                if (sentryTraceHeader) {
                    config.headers['sentry-trace'] = sentryTraceHeader;
                }

                if (!config.headers['baggage'] && sentryBaggageHeader) {
                    config.headers['baggage'] = sentryBaggageHeader;
                }
            }
        }

        config.headers['x-stream-id'] = uuid();

        const skipAuth = (config as ICockpitAxiosRequestConfig).skipAuth;
        if (skipAuth) {
            return config;
        }

        return authManager
            .getToken()
            .then(token => {
                if (token) {
                    config.headers!.Authorization = `Bearer ${token}`;
                }

                return config;
            })
            .catch(() => config);
    };

const onRejectedResponse = async (error: any): Promise<any> => {
    if (axios.isCancel(error)) {
        return Promise.reject(error);
    }

    if (!shouldBeHandledInComponent(error)) {
        await maybeShowAxiosError(error);
    }

    return Promise.reject(error);
};

export const maybeShowAxiosError = async (error: Error) => {
    if (axios.isAxiosError(error)) {
        const data = error?.response?.data as Record<string, any>;
        const streamId = data?.debug?.streamId;
        if (data?.debug?.consoleLink) {
            console.error(`Error happened, here is the cloud-logging link: ${data?.debug?.consoleLink}`);
        }
        const innerMostError = getInnerMostError(error);
        const errorMessage = innerMostError?.message ?? 'Unknown error occurred';
        if (innerMostError?.tags?.includes(ErrorTag.FRONTEND_SILENT)) {
            return Promise.reject(error);
        }
        if (error?.response?.data instanceof Blob && error?.response?.data?.type === 'application/json') {
            showErrorMessage({ message: errorMessage, responseData: JSON.parse(await error.response.data.text()), streamId });
        } else {
            showErrorMessage({ message: errorMessage, responseData: data, streamId });
        }
    } else {
        showErrorMessage({ message: error.message });
    }
};

const shouldBeHandledInComponent = (error: any) => {
    const code = error?.response?.data?.data?.context?.code;
    if (error?.config?.skipErrorHandler) {
        return true;
    }

    if (axios.isAxiosError(error)) {
        if (store.getters['isExternal'] && error.response?.config.method?.toLowerCase() === 'get') {
            return true;
        }
    }

    return !isNil(code) && ([ErrorCode.DuplicateInvoice, ErrorCode.RentRollVersionReferencedByInvestmentCases] as string[]).includes(code);
};

/**
 * @note This is a circular dependency to the store (all stores invoke api-methods
 * which use this class, directly using the store here is not possible)
 */
export const initBasecampApiClient = (store: Store<ICockpitStore>) => {
    clients.basecamp = extendAxiosClient(store, 'S_BASECAMP_URL');
    clients.basecampMaintenance = extendAxiosClient(store, 'S_BASECAMP_MAINT_URL', true);
    clients.basecampPersister = extendAxiosClient(store, 'S_BASECAMP_PERSISTER_URL', true);
};

/**
 * @note This is a circular dependency to the store (all stores invoke api-methods
 * which use this class, directly using the store here is not possible)
 */
export const initDiscovererApiClient = (store: Store<ICockpitStore>) => {
    clients.discoverer = extendAxiosClient(store, 'S_DISCOVERER_URL');
};

export const initExternalApiClient = (store: Store<ICockpitStore>) => {
    const client = extendAxiosClient(store, 'S_EXTERNAL_API_URL');
    const sdk = new ExternalApiSdk(client);

    clients.externalApi = sdk;
    clients.externalApiHttp = client;
};

export const initPublicHttpClients = (cockpitStore: Store<ICockpitStore> = store) => {
    initBasecampApiClient(cockpitStore);
    initDiscovererApiClient(cockpitStore);
    initExternalApiClient(cockpitStore);
    initListingPersisterClient(cockpitStore);
    initSalesPortalApiClient(cockpitStore);
    initPropxApiClient(cockpitStore);
    initSourcingStorageClient(cockpitStore);
    initPipelineAuditPersisterClient(cockpitStore);
    initCalcEngineApiClient(cockpitStore);
};

export const initListingPersisterClient = (store: Store<ICockpitStore>) => {
    const client = extendAxiosClient(store, 'S_LISTING_PERSISTER_URL');
    const sdk = new ListingPersisterSdk(client);

    clients.listingPersister = sdk;
};

export const initSalesPortalApiClient = (store: Store<ICockpitStore>) => {
    clients.salesPortal = extendAxiosClient(store, 'S_SALES_PORTAL_API_URL');
};

export const initPropxApiClient = (store: Store<ICockpitStore>) => {
    const client = extendAxiosClient(store, 'S_PROPX_URL');
    const sdk = new PropxSdk(client);

    clients.propx = sdk;
};

export const initSourcingStorageClient = (store: Store<ICockpitStore>) => {
    const client = extendAxiosClient(store, 'S_SOURCING_STORAGE_URL');
    const sdk = new SourcingStorageSdk(client);

    clients.sourcingStorage = sdk;
};

export const initTypesenseClient = (store: Store<ICockpitStore>) => {
    const typesenseConfig = store.getters['getTypesenseConfig'] as { url: string; apiKey: string };

    if (typesenseConfig.url && typesenseConfig.apiKey) {
        clients.typesenseClient = new TypesenseClient({
            nodes: [{ url: typesenseConfig.url }],
            apiKey: typesenseConfig.apiKey,
            connectionTimeoutSeconds: 3,
        });
    }
};

export const initPipelineAuditPersisterClient = (store: Store<ICockpitStore>) => {
    const client = extendAxiosClient(store, 'S_PIPELINE_AUDIT_PERSISTER_URL');
    const sdk = new PipelineAuditPersisterSdk(client);

    clients.pipelineAuditPersister = sdk;
};

export const initThemergerApiClient = (store: Store<ICockpitStore>) => {
    const client = axios.create({
        paramsSerializer: {
            serialize: params => qs.stringify(params, { arrayFormat: 'repeat' }),
        },
    });

    client.interceptors.request.use(config => {
        const appConfig = store.getters['generalData/config'] as ICockpitConfig;

        config.baseURL = appConfig['S_THEMERGER_API_URL'];
        config.headers['API-Key'] = `${appConfig['THEMERGER_API_KEY']}`;
        return config;
    });

    clients.themerger = client;
};

export const initCalcEngineApiClient = (store: Store<ICockpitStore>) => {
    const client = extendAxiosClient(store, 'S_CALCENGINE_URL');
    const sdk = new CalcengineSdkWrapper(client);

    clients.calcengine = sdk;
};

export const initHereCacheApiClient = (store: Store<ICockpitStore>) => {
    const client = axios.create({
        paramsSerializer: {
            serialize: params => qs.stringify(params, { arrayFormat: 'repeat' }),
        },
    });

    client.interceptors.request.use(config => {
        const appConfig = store.getters['generalData/config'] as ICockpitConfig;

        config.baseURL = appConfig.S_HERE_CACHE_URL;
        config.headers.Authorization = `Bearer ${appConfig.HERE_CACHE_KEY}`;
        config.headers['X-Service-Name'] = 'cockpit';

        return config;
    });

    client.interceptors.response.use(response => response, onRejectedResponse);

    clients.hereCache = client;
};

export const initHttpClients = (cockpitStore: Store<ICockpitStore> = store) => {
    initThemergerApiClient(cockpitStore);
    initHereCacheApiClient(cockpitStore);
};

export const getClient = <TIdentifier extends keyof IApiClients>(identifier: TIdentifier): IApiClients[TIdentifier] => {
    const client = clients[identifier];

    if (!client) {
        initConfiguredHttpClients();
        const configuredClient = clients[identifier];
        if (!configuredClient) {
            throw new Error(`Client "${identifier}" is not available for the current config state`);
        }
        return configuredClient;
    }

    return client;
};

if (import.meta.hot) {
    import.meta.hot.accept(() => {
        import.meta.hot!.data.clients = clients;
    });
}
